// noinspection JSUnusedGlobalSymbols

import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, filter, map, of, switchMap } from "rxjs";
import { RootState } from "../reducers/root.reducer";
import { ApiHttpService } from "../services/api.service";
import {
    openEditUser,
    openInfo,
    openPlaid,
    plaidClosedNotFinished,
    plaidOpened,
    processPlaid,
} from "../actions/app.actions";
import { InfoPopupComponent } from "../components/popups/info-popup/info-popup.component";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import type { Plaid } from "plaid-link";
import { logout, setUser } from "../actions/user.actions";
import { StripeService } from "../services/stripe.service";
import { UserSession } from "@home-shark/types";
import { env } from "../../env";
import { openLoginDialog } from "../modules/overview/lib/dialogs.lib";
import { EditUserComponent } from "../components/popups/edit-user-popup/edit-user-popup.component";

type LinkHandler = Plaid.LinkHandler;
type CreateConfig = Plaid.CreateConfig;

@Injectable()
export class AppEffects {
    public actions$ = inject(Actions);
    private infoDialog: MatDialogRef<InfoPopupComponent>;
    private editDialog: MatDialogRef<EditUserComponent>;
    private linkHandler!: LinkHandler;
    public platformId = inject(PLATFORM_ID);

    // Replace with your own public key

    constructor(
        public store: Store<RootState>,
        public api: ApiHttpService,
        public dialog: MatDialog,
        private stripeService: StripeService
    ) {}

    openInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openInfo), // Triggered when loadUser action is dispatched
            map((action) => {
                if (this.infoDialog) this.infoDialog.close();
                this.infoDialog = this.dialog.open(InfoPopupComponent, {
                    width: action.width,
                    height: action.height,
                    enterAnimationDuration: 100,
                    exitAnimationDuration: 100,
                    data: action.data,
                });
                return null as any;
            }),
            filter((action) => !!action)
        )
    );

    openEditUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openEditUser),
            map((action) => {
                if (this.editDialog) this.editDialog.close();
                this.editDialog = this.dialog.open(EditUserComponent, {
                    width: "40rem",
                    height: "34rem",
                    enterAnimationDuration: 100,
                    exitAnimationDuration: 100,
                });
                return null as any;
            }),
            filter((action) => !!action)
        )
    );

    openPlaid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openPlaid),
            switchMap((action) => {
                return this.api
                    .post<any>("/verification/create", { method: "POST" })
                    .pipe(
                        catchError((err: any) => {
                            //TODO: Display Fail Window
                            console.log("Failed to create Plaid link");
                            if (err?.status === 401) {
                                openLoginDialog(this.dialog);
                                return of(logout({}));
                            }
                            return of(null);
                        }),
                        filter((res: any) => !!res?.["link_token"]),
                        switchMap((res: any) => {
                            const config: CreateConfig = {
                                env: env.production
                                    ? "production"
                                    : "development",
                                clientName: "My Verification Flow",
                                product: ["identity_verification"],
                                token: res.link_token,
                                onSuccess: (public_token, metadata) => {
                                    console.log(
                                        "Success!",
                                        public_token,
                                        metadata
                                    );
                                    if (this.linkHandler.destroy)
                                        this.linkHandler.destroy();

                                    this.store.dispatch(
                                        processPlaid({
                                            session_id:
                                                metadata.link_session_id,
                                        })
                                    );
                                },
                                onExit: (err, metadata) => {
                                    if (this.linkHandler.destroy)
                                        this.linkHandler.destroy();

                                    console.warn("Exited", err, metadata);
                                    // TODO: Inform the user that they need to finish later
                                    this.store.dispatch(
                                        plaidClosedNotFinished()
                                    );
                                },
                            };
                            this.linkHandler = (window as any).Plaid.create(
                                config
                            );
                            setTimeout(() => {
                                this.linkHandler.open();
                            }, 0);
                            return of(plaidOpened());
                        })
                    );
            })
        )
    );

    processPlaid$ = createEffect(() =>
        this.actions$.pipe(
            ofType(processPlaid),
            switchMap((action) => {
                return this.api
                    .post<any>("/verification/process", {
                        session_id: action.session_id,
                    })
                    .pipe(
                        catchError((err: any) => {
                            //TODO: Display Fail Window
                            console.log("Failed to process Plaid link");
                            if (err?.status === 401) {
                                openLoginDialog(this.dialog);
                                return of(logout({}));
                            }
                            return of(null);
                        }),
                        filter((res) => !!res),
                        switchMap((res: any) => {
                            if (res.type) return of(res);
                            // If successful the updated jwt should be returned
                            else if (res?.userSession?.id) {
                                //TODO: If the response had the user then the token should be reset and the user session reloaded
                                return of(
                                    setUser({
                                        user: res.userSession as UserSession,
                                    })
                                );
                            }
                            console.log("Failed to process Plaid link");
                            return of(null);
                        }),
                        filter((res) => !!res)
                    );
            })
        )
    );
}
